import { compact } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  BULK_COPY_LIMIT,
  BULK_DELETE_LIMIT,
  BULK_EDIT_CLAUSE_LIMIT,
  BULK_EDIT_LIMIT,
  BULK_MOVE_LIMIT,
} from '~/common/constants/max_lengths';
import { useLimitExceededModal } from '~/components/DocumentsPage/DocumentsRestore/useLimitExceededModal';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { PilotId } from '~/eds/src/types/PilotId';
import { TableState } from '~/eds/src/types/Table';
import { BulkActionType } from '~/enums/BulkActionType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePermission } from '~/hooks/usePermission';
import { PilotSearchQuery } from '~/redux/api/methods/search';
import { testContainsGhostSearchResult } from '~/utils/document.utils';

import { DocumentHandler } from '../advanced-search/types';
import { AddSearchResultsToGroup } from './AddSearchResultsToGroup/AddSearchResultsToGroup';
import { DeleteActionPanel } from './BulkActionPanels/DeleteActionPanel';
import { EditClausesActionPanel } from './BulkActionPanels/EditClausesActionPanel';
import { EditFieldsActionPanel } from './BulkActionPanels/EditFieldsActionPanel';
import { MoveCopyActionPanel } from './BulkActionPanels/MoveCopyActionPanel';
import { BaseBulkActionProps } from './types/bulk-actions';

type BaseDocumentSearchItem = { document_handlers: DocumentHandler[] };
interface Props extends BaseBulkActionProps {
  resultsCache: Record<PilotId, BaseDocumentSearchItem>;
  totalCount: number;
  /** this prop will be passed to MoveCopy panel to disable folders containing sync pair. only applicable when
   * moving folders.
   */
  shouldDisableSyncPairedFolders?: boolean;
}

export const useDocumentsBulkActionsPanel = ({
  isAllSelected,
  resultsCache,
  searchAPI,
  selectedDocumentIds = [],
  shouldDisableSyncPairedFolders = false,
  totalCount,
  query,
  onActionCompleted: onActionCompletedParam,
}: Props) => {
  const intl = useIntl();
  const [activePanel, setActivePanel] = useState<BulkActionType | undefined>();
  const [hasCheckedLimit, setHasCheckedLimit] = useState(false);
  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const closePanel = () => {
    setActivePanel(undefined);
    setHasCheckedLimit(false);
  };

  const hasGroupsEditPermission = usePermission({
    permission: {
      resourceId: 'document_groups',
      resourceType: 'edit',
    },
  });

  const documentHandlerIds = useMemo(() => {
    //only edit clauses action receives a document id list. The rest of the actions receive a doc handler id list.
    return selectedDocumentIds.reduce((acc, current) => {
      const document = resultsCache[current];
      if (document) {
        return acc.concat(
          document.document_handlers?.map((handler) => handler.id as PilotId),
        );
      }
      return acc;
    }, [] as PilotId[]);
  }, [selectedDocumentIds, resultsCache]);

  const handleHideLimitExceededModal = () => {
    closeLimitExceededModal();
    closePanel();
  };

  const numberOfAffectedDocuments = isAllSelected
    ? totalCount
    : selectedDocumentIds.length;

  const [limitExceededModal, showLimitExceededModal, closeLimitExceededModal] =
    useLimitExceededModal({
      limit: getBulkActionDocumentsLimit(activePanel),
      actionType: activePanel ?? 'copy',
      count: numberOfAffectedDocuments,
      primaryActionHide: handleHideLimitExceededModal,
    });

  const onActionCompleted = () => {
    onActionCompletedParam?.();
    closePanel();
  };

  useEffect(() => {
    if (activePanel) {
      if (
        numberOfAffectedDocuments > getBulkActionDocumentsLimit(activePanel)
      ) {
        showLimitExceededModal();
      } else {
        setHasCheckedLimit(true);
      }
    }
  }, [activePanel, numberOfAffectedDocuments]);

  const baseBulkActionProps: BaseBulkActionProps = {
    isAllSelected,
    onActionCompleted,
    query,
    searchAPI: searchAPI,
    selectedDocumentIds:
      activePanel !== 'edit-clauses' && activePanel !== 'edit'
        ? documentHandlerIds
        : selectedDocumentIds,
  };

  const panelComponent = useMemo(() => {
    switch (activePanel) {
      case 'copy':
      case 'move':
        return (
          <MoveCopyActionPanel
            {...baseBulkActionProps}
            selectedCount={numberOfAffectedDocuments}
            shouldDisableSyncPairedFolders={shouldDisableSyncPairedFolders}
            onHide={closePanel}
            activePanel={activePanel}
          />
        );
      case 'delete':
        return (
          <DeleteActionPanel
            {...baseBulkActionProps}
            onHide={closePanel}
            selectedCount={numberOfAffectedDocuments}
          />
        );
      case 'edit':
        return (
          <EditFieldsActionPanel
            selectedCount={numberOfAffectedDocuments}
            {...baseBulkActionProps}
            onHide={closePanel}
            selectedDocumentHandlerIds={documentHandlerIds}
          />
        );

      case 'edit-clauses':
        return (
          <EditClausesActionPanel
            onHide={closePanel}
            selectedCount={numberOfAffectedDocuments}
            {...baseBulkActionProps}
          />
        );
      case 'add-to-group':
        return (
          <AddSearchResultsToGroup
            query={query as PilotSearchQuery}
            tableSelection={
              {
                selectedIds: selectedDocumentIds as string[],
                isAllSelected: !!isAllSelected,
              } as ReturnType<typeof useTableSelection>
            }
            onHide={closePanel}
            onCompleted={onActionCompleted}
          />
        );
    }
  }, [activePanel]);

  const getBulkActions = ({ selectedRowIds }: TableState) => {
    const isAnyNotMovable = false;
    const documents = selectedRowIds?.map((id) => resultsCache[id]);
    const containsGhost = testContainsGhostSearchResult(documents);
    // TODO: Confirm the visible no edit check for bulk actions

    const bulkActions = compact([
      {
        icon: 'folder' as const,
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.movePrivilegesError',
              defaultMessage:
                'You do not have privileges to move one or more of the items you have selected.',
              description:
                'Error message when user lacks move privileges for selected items',
            })
          : intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.moveLabel',
              defaultMessage: 'Move',
              description: 'Label for move bulk action',
            }),
        onClick: () => setActivePanel('move'),
        disabled: isAnyNotMovable,
        value: null,
      },
      {
        icon: 'clipboard' as const,
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.copyPrivilegesError',
              defaultMessage:
                'You do not have privileges to copy one or more of the items you have selected.',
              description:
                'Error message when user lacks copy privileges for selected items',
            })
          : intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.copyLabel',
              defaultMessage: 'Copy',
              description: 'Label for copy bulk action',
            }),
        onClick: () => setActivePanel('copy'),
        disabled: isAnyNotMovable,
        value: null,
      },
      {
        icon: 'edit' as const,
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.editFieldsPrivilegesError',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description:
                'Error message when user lacks edit privileges for selected items',
            })
          : intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.editFieldsLabel',
              defaultMessage: 'Edit Fields',
              description: 'Label for edit fields bulk action',
            }),
        onClick: () => setActivePanel('edit'),
        disabled: isAnyNotMovable,
        value: null,
      },
      {
        icon: 'edit' as const,
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.editClausesPrivilegesError',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description:
                'Error message when user lacks edit privileges for selected items',
            })
          : intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.editClausesLabel',
              defaultMessage: 'Edit Clauses',
              description: 'Label for edit clauses bulk action',
            }),
        onClick: () => setActivePanel('edit-clauses'),
        disabled: isAnyNotMovable,
        value: null,
      },
      {
        icon: 'trash' as const,
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.deletePrivilegesError',
              defaultMessage:
                'You do not have privileges to delete one or more of the items you have selected.',
              description:
                'Error message when user lacks delete privileges for selected items',
            })
          : intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.deleteLabel',
              defaultMessage: 'Delete',
              description: 'Label for delete bulk action',
            }),
        onClick: () => setActivePanel('delete'),
        disabled: isAnyNotMovable,
        value: null,
      },
      !hasGroupedTicketAndDocs && {
        icon: 'groups' as const,
        label: containsGhost
          ? intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.addToGroupPrivilegesError',
              defaultMessage:
                'You do not have privileges to add one or more of the items you have selected to a group.',
              description:
                'Error message when user lacks privileges to add items to group',
            })
          : intl.formatMessage({
              id: 'DocumentsBulkActionsPanel.addToGroupLabel',
              defaultMessage: 'Add to Group',
              description: 'Label for add to group bulk action',
            }),
        onClick: () => setActivePanel('add-to-group'),
        disabled: containsGhost || !hasGroupsEditPermission,
        value: null,
      },
    ]);

    return bulkActions;
  };

  return {
    panel: activePanel && hasCheckedLimit ? panelComponent : null,
    limitExceededModal,
    getBulkActions,
    setActivePanel,
  };
};

const getBulkActionDocumentsLimit = (bulkAction?: BulkActionType) => {
  switch (bulkAction) {
    case 'copy':
      return BULK_COPY_LIMIT;
    case 'delete':
      return BULK_DELETE_LIMIT;
    case 'edit':
      return BULK_EDIT_LIMIT;
    case 'edit-clauses':
      return BULK_EDIT_CLAUSE_LIMIT;
    case 'move':
      return BULK_MOVE_LIMIT;
    default:
      return Number.POSITIVE_INFINITY;
  }
};
